import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import LinearProgress from "@mui/material/LinearProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { api, getCsrfToken, unwrap } from "../api/client";
import { WmsOverlays } from "./WmsOverlays";

const MAPS_KEY = ["maps"] as const;

const size = (b: number) =>
  b >= 1e9 ? `${(b / 1e9).toFixed(1)} GB` : `${(b / 1e6).toFixed(1)} MB`;

/** XMLHttpRequest, not fetch: only it reports upload progress. */
function upload(
  file: File,
  name: string,
  onProgress: (done: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `/api/maps?name=${encodeURIComponent(name)}`);
    xhr.setRequestHeader("Content-Type", "application/octet-stream");
    const token = getCsrfToken();
    if (token) xhr.setRequestHeader("X-CSRF-Token", token);
    xhr.upload.onprogress = (e) =>
      e.lengthComputable && onProgress(e.loaded / e.total);
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) return resolve();
      let message = `HTTP ${xhr.status}`;
      try {
        message =
          (JSON.parse(xhr.responseText) as { message?: string }).message ??
          message;
      } catch {
        // Not JSON: keep the status.
      }
      reject(new Error(message));
    };
    xhr.onerror = () => reject(new Error("the upload failed"));
    xhr.send(file);
  });
}

/** Offline base maps (design §9.1): PMTiles (preferred), MBTiles or GeoPackage packages. */
export function MapsPage() {
  const qc = useQueryClient();
  const maps = useQuery({
    queryKey: MAPS_KEY,
    queryFn: () => unwrap(api.GET("/api/maps")),
  });
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState("");
  const [progress, setProgress] = useState<number | null>(null);

  const add = useMutation({
    mutationFn: ({ f, n }: { f: File; n: string }) => upload(f, n, setProgress),
    onSettled: () => setProgress(null),
    onSuccess: () => {
      setFile(null);
      setName("");
      void qc.invalidateQueries({ queryKey: MAPS_KEY });
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) =>
      unwrap(api.DELETE("/api/maps/{id}", { params: { path: { id } } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: MAPS_KEY }),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (file)
      add.mutate({
        f: file,
        n: name.trim() || file.name.replace(/\.[^.]+$/, ""),
      });
  };

  return (
    <Stack spacing={3} component="section">
      <Typography variant="h5" component="h2">
        Maps
      </Typography>
      <Typography color="text.secondary">
        Base maps used offline on the Timeline map. Import a PMTiles
        (preferred), MBTiles or GeoPackage package, for example an extract made
        with the pmtiles tool or Planetiler. A GeoPackage must use the web map
        grid (EPSG:3857, as GDAL writes with
        TILING_SCHEME=GoogleMapsCompatible). The package is checked, stored on
        the server and recorded in the audit log.
      </Typography>
      <Paper
        variant="outlined"
        sx={{ p: 2 }}
        component="form"
        onSubmit={submit}
      >
        <Stack
          direction="row"
          sx={{ gap: 2, alignItems: "center", flexWrap: "wrap" }}
        >
          <Button variant="outlined" component="label">
            Choose package
            <input
              hidden
              type="file"
              accept=".pmtiles,.mbtiles,.gpkg"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </Button>
          <Typography variant="body2" sx={{ minWidth: 160 }}>
            {file ? `${file.name} (${size(file.size)})` : "No file chosen"}
          </Typography>
          <TextField
            size="small"
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={file?.name.replace(/\.[^.]+$/, "")}
          />
          <Button
            type="submit"
            variant="contained"
            disabled={!file || add.isPending}
          >
            Import
          </Button>
        </Stack>
        {progress !== null && (
          <LinearProgress
            variant="determinate"
            value={progress * 100}
            sx={{ mt: 2 }}
            aria-label="Upload progress"
          />
        )}
        {add.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {add.error.message}
          </Alert>
        )}
      </Paper>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" aria-label="Base maps">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Format</TableCell>
              <TableCell>Zoom</TableCell>
              <TableCell>Area (W, S, E, N)</TableCell>
              <TableCell>Size</TableCell>
              <TableCell>Imported</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {maps.data?.length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>No base maps yet.</TableCell>
              </TableRow>
            )}
            {maps.data?.map((m) => (
              <TableRow key={m.id}>
                <TableCell>{m.name}</TableCell>
                <TableCell>
                  {m.format} ({m.tile_type === "mvt" ? "vector" : m.tile_type})
                </TableCell>
                <TableCell>
                  {m.min_zoom}–{m.max_zoom}
                </TableCell>
                <TableCell>
                  {m.bounds.map((v) => v.toFixed(2)).join(", ")}
                </TableCell>
                <TableCell>{size(m.size_bytes)}</TableCell>
                <TableCell>
                  {new Date(m.created_at)
                    .toISOString()
                    .slice(0, 16)
                    .replace("T", " ")}{" "}
                  UTC
                </TableCell>
                <TableCell>
                  {!m.builtin && (
                    <Button
                      size="small"
                      color="error"
                      disabled={remove.isPending}
                      onClick={() => {
                        if (window.confirm(`Delete the base map "${m.name}"?`))
                          remove.mutate(m.id);
                      }}
                    >
                      Delete
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <WmsOverlays />
    </Stack>
  );
}
